import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import ManeLogo from '../components/Logo';
import { FEATURED_BARBER_TESTIMONIALS } from '../data/reviews';

// ── Brand ──────────────────────────────────────────────────────────────────
const BLUE = '#2B7FFF';
const BLUE_DIM = 'rgba(43,127,255,0.12)';
const BLUE_BORDER = 'rgba(43,127,255,0.25)';
const GOLD = '#C9A84C';
const GOLD_DIM = 'rgba(201,168,76,0.12)';
const WHITE = '#FFFFFF';
const OFF_WHITE = '#A0AABB';
const BG = '#050810';
const SURFACE = '#0B1120';
const CARD = '#0F1929';
const BORDER = 'rgba(255,255,255,0.07)';

// ── Hooks ──────────────────────────────────────────────────────────────────
function useScrollReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function useCounter(target, duration = 1800, active = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = null;
    const step = ts => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      setCount(Math.floor(p * target));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [active, target, duration]);
  return count;
}

// ── CSS ────────────────────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

  .mane-page * { box-sizing: border-box; margin: 0; padding: 0; }
  .mane-page { width: 100%; max-width: 100vw; overflow-x: hidden; }

  /* ── Mobile baseline ── */
  @media (max-width: 768px) {
    .mane-page h1 { word-break: break-word; }
    .mane-page nav { padding: 0 12px !important; gap: 6px !important; }
    .mane-page section { padding-left: 16px !important; padding-right: 16px !important; }
    .mane-nav-btn { padding: 8px 12px !important; font-size: 12px !important; white-space: nowrap; border-radius: 6px !important; }
    .mane-card { padding: 20px !important; }
    .mane-reveal { opacity: 1 !important; transform: none !important; }
  }
  @media (max-width: 480px) {
    .mane-nav-btn { padding: 6px 10px !important; font-size: 11px !important; }
    .mane-cta-primary { width: 100% !important; padding: 16px 24px !important; font-size: 14px !important; }
    .mane-submit { font-size: 14px !important; padding: 16px !important; }
  }

  @keyframes fadeUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
  @keyframes shimmer { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
  @keyframes pulse-blue { 0%,100%{box-shadow:0 0 0 0 rgba(43,127,255,0.4)} 60%{box-shadow:0 0 0 16px rgba(43,127,255,0)} }
  @keyframes bounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(8px)} }
  @keyframes float { 0%,100%{transform:translateY(0px) rotate(-1deg)} 50%{transform:translateY(-14px) rotate(1deg)} }
  @keyframes glow-cta { 0%,100%{box-shadow:0 0 20px rgba(43,127,255,0.4),0 4px 24px rgba(0,0,0,0.4)} 50%{box-shadow:0 0 40px rgba(43,127,255,0.7),0 4px 32px rgba(0,0,0,0.5)} }
  @keyframes gradient-border { 0%{border-color:rgba(43,127,255,0.5)} 50%{border-color:rgba(201,168,76,0.6)} 100%{border-color:rgba(43,127,255,0.5)} }
  @keyframes slide-in-right { from{opacity:0;transform:translateX(40px)} to{opacity:1;transform:translateX(0)} }

  .mane-cta-primary {
    padding:18px 48px; border-radius:12px; border:none;
    background:${BLUE}; color:#fff;
    font-weight:800; font-size:15px; letter-spacing:0.06em;
    cursor:pointer; font-family:inherit;
    animation: glow-cta 3s ease-in-out infinite;
    transition: transform 0.15s, opacity 0.15s;
  }
  .mane-cta-primary:hover { transform:translateY(-3px) scale(1.02); opacity:0.95; }

  .mane-feature-icon {
    width:52px; height:52px; border-radius:14px;
    display:flex; align-items:center; justify-content:center;
    font-size:22px; margin-bottom:20px;
    background:linear-gradient(135deg, rgba(43,127,255,0.15) 0%, rgba(43,127,255,0.05) 100%);
    border:1px solid rgba(43,127,255,0.2);
  }

  .mane-avatar {
    width:40px; height:40px; border-radius:50%;
    display:flex; align-items:center; justify-content:center;
    font-weight:800; font-size:13px; letter-spacing:0.04em;
    flex-shrink:0;
  }

  .mane-pro-card {
    animation: gradient-border 4s ease-in-out infinite !important;
  }

  .mane-mockup {
    animation: float 6s ease-in-out infinite;
    animation: slide-in-right 0.8s ease 1s both, float 6s ease-in-out 1.8s infinite;
  }

  .mane-reveal { opacity:0; transform:translateY(24px); transition:opacity 0.7s ease, transform 0.7s ease; }
  .mane-reveal.in { opacity:1; transform:translateY(0); }

  .mane-input {
    width:100%; background:rgba(255,255,255,0.04);
    border:1px solid rgba(255,255,255,0.08);
    border-radius:10px; padding:14px 16px; color:#fff;
    font-size:15px; outline:none; box-sizing:border-box;
    transition:border-color 0.2s; font-family:'Inter',sans-serif;
  }
  .mane-input:focus { border-color:${BLUE}; }
  .mane-input::placeholder { color:#7A8499; }
  .mane-input option { background:#0B1120; color:#fff; }

  .mane-card {
    background:${CARD};
    border:1px solid ${BORDER};
    border-radius:16px;
    padding:32px;
    transition:border-color 0.25s ease;
  }
  .mane-card:hover { border-color:${BLUE_BORDER}; }

  .mane-service-row {
    display:flex; align-items:center; justify-content:space-between;
    padding:18px 22px;
    background:${SURFACE};
    border:1px solid ${BORDER};
    border-radius:10px;
    text-decoration:none;
    transition:border-color 0.2s, background 0.2s;
    cursor:pointer;
  }
  .mane-service-row:hover { border-color:${BLUE_BORDER}; background:${CARD}; }

  .mane-submit {
    width:100%; padding:18px; border-radius:12px; border:none; cursor:pointer;
    background:${BLUE}; color:#fff;
    font-weight:800; font-size:15px; letter-spacing:0.06em;
    font-family:'Inter',sans-serif;
    transition:opacity 0.2s, transform 0.15s;
  }
  .mane-submit:hover:not(:disabled) { opacity:0.9; transform:translateY(-1px); }
  .mane-submit:disabled { opacity:0.35; cursor:not-allowed; }

  /* Subtle grid bg */
  .mane-grid-bg {
    background-image:
      linear-gradient(rgba(43,127,255,0.025) 1px, transparent 1px),
      linear-gradient(90deg, rgba(43,127,255,0.025) 1px, transparent 1px);
    background-size:64px 64px;
  }
`;

// ── Data ───────────────────────────────────────────────────────────────────
const INDUSTRIES = ['Barbers','Stylists','Nail Techs','Lash Techs','Tattoo Artists','Estheticians','Coaches','You'];

const PAIN_POINTS = [
  { icon: '📱', title: 'Your phone owns you', desc: 'DMs at dinner. Booking confirmations at bedtime. Follow-ups between clients. You went independent for freedom — but you\'re more chained than ever.' },
  { icon: '⏰', title: 'Time you can\'t get back', desc: 'The average service pro spends 3 hours a day on admin. That\'s Sunday mornings with your kids. Dinners you\'re half-present for. Moments you can\'t buy back.' },
  { icon: '🔄', title: 'The trap nobody talks about', desc: 'You got booked. You got busy. And then somehow being your own boss started feeling like you work for everyone but yourself. That\'s not a business. That\'s a trap.' },
];

const FEATURES = [
  { icon: '📅', title: 'Booking While You Sleep', desc: 'Your booking link works at 2am. Clients book themselves. You wake up to a full calendar — no DMs, no back-and-forth.' },
  { icon: '💳', title: 'Deposits on Autopilot', desc: 'Deposits collect automatically. No-shows get charged automatically. Zero chasing, zero awkward conversations.' },
  { icon: '💬', title: 'Follow-ups Send Themselves', desc: 'Post-visit thanks, rebook reminders, win-back messages. All automated. You\'re home. The business keeps running.' },
  { icon: '👥', title: 'Your Clients. Always Yours.', desc: 'Your client list belongs to you — not the platform. Export anytime. Nobody can take what you built.' },
  { icon: '🤖', title: 'AI Runs the Front Desk', desc: 'AI handles booking questions, reminders, and follow-ups around the clock. Like having a receptionist who never sleeps.' },
  { icon: '💰', title: '0% Commission. Forever.', desc: "Every dollar from every booking goes to you. Not a platform. Not a percentage. Yours." },
];

const TIERS = [
  {
    name: 'Starter', price: '$29', period: '/mo', setup: '$149 one-time setup',
    features: [
      'Online booking 24/7',
      'Custom booking page (yourname.mane.com)',
      'Full client management + notes',
      'Deposits & no-show protection',
      'Service menu with pricing',
      'Email appointment reminders',
      'Basic analytics dashboard',
      '0% commission — forever',
    ],
    cta: 'Claim Your Spot', featured: false,
  },
  {
    name: 'Pro', price: '$59', period: '/mo', setup: '$149 one-time setup',
    features: [
      'Everything in Starter',
      'SMS reminders (automated)',
      'AI booking assistant 24/7',
      'Advanced revenue analytics',
      'Client retention & at-risk alerts',
      'Auto review requests after every appointment',
      'Loyalty & rewards system',
      'Priority support',
    ],
    cta: 'Claim Your Spot', featured: true,
  },
  {
    name: 'Agency', price: '$45', period: '/seat/mo', setup: 'Min. 3 seats · Custom onboarding',
    features: [
      'Everything in Pro',
      'Per-seat pricing — scale as your team grows',
      'White-label booking page',
      'Dedicated account manager',
      'Custom onboarding & training',
      'API access',
      'Team analytics dashboard',
      'SLA-backed support',
    ],
    cta: 'Claim Your Spot', featured: false, accent: 'rgba(120,80,255,0.8)',
  },
];

const ADDONS = [
  { icon: '🤖', name: 'AI Assistant', desc: '24/7 AI receptionist — handles booking questions, follow-ups, and client communication automatically.', price: '+$29/mo', live: true },
  { icon: '📊', name: 'CRM & Outreach', desc: 'Automated re-engagement emails — post-visit thanks, 21-day rebook nudge, 45-day win-back. Runs itself.', price: '+$29/mo', live: true },
  { icon: '📱', name: 'Social Manager', desc: 'AI-generated captions & posts from your work photos. Schedule across all platforms.', price: '+$39/mo', live: false },
];

const TESTIMONIALS = FEATURED_BARBER_TESTIMONIALS.map(r => ({
  quote: r.text,
  name: r.name,
  city: r.date || r.source,
  source: r.source,
}));

const SERVICES = [
  { name: 'The G Classic', price: '$45', tag: 'POPULAR' },
  { name: 'The G Classic + Beard', price: '$55', tag: 'BEST VALUE' },
  { name: 'The G Deluxe', price: '$100', tag: 'FULL EXPERIENCE' },
  { name: "Kids Cut", price: 'from $35', tag: null },
  { name: 'Mobile / Event', price: 'from $150', tag: 'WE COME TO YOU' },
];

// ── iPhone Mockup — single living screen with micro-interactions ─────────
function IPhoneMockup() {
  const [step, setStep] = React.useState(0);
  // Timed micro-interactions that loop
  // 0: idle (stats visible)
  // 1: new booking notification slides in (1.5s)
  // 2: notification accepted, appointment appears in list (2s)
  // 3: AI insight updates (2s)
  // 4: revenue number ticks up (1.5s)
  // then reset
  React.useEffect(() => {
    const sequence = [
      setTimeout(() => setStep(1), 2000),
      setTimeout(() => setStep(2), 4000),
      setTimeout(() => setStep(3), 6500),
      setTimeout(() => setStep(4), 9000),
      setTimeout(() => setStep(0), 11500),
    ];
    const loop = setInterval(() => {
      setStep(0);
      setTimeout(() => setStep(1), 2000);
      setTimeout(() => setStep(2), 4000);
      setTimeout(() => setStep(3), 6500);
      setTimeout(() => setStep(4), 9000);
    }, 11500);
    return () => { sequence.forEach(clearTimeout); clearInterval(loop); };
  }, []);

  const revenueDisplay = step >= 4 ? '$430' : '$385';
  const newBooking = step >= 1 && step < 4;
  const bookingAccepted = step >= 2;
  const insightText = step >= 3
    ? 'New booking confirmed. You\'re fully booked until 2pm.'
    : 'Tuesdays are up 23%. Open an extra 2pm slot to capture demand.';

  return (
    <div className="mane-iphone-wrap" style={{ flex:'0 0 auto', width:'280px', perspective:'1000px' }}>
      <div className="mane-mockup" style={{
        position:'relative', width:'280px', height:'572px',
        background:'linear-gradient(145deg, #2a2a2e 0%, #1a1a1e 50%, #0f0f12 100%)',
        borderRadius:'52px', padding:'10px',
        boxShadow:'0 50px 100px rgba(0,0,0,0.9), 0 0 0 1px rgba(255,255,255,0.05), inset 0 0 0 1px rgba(255,255,255,0.03), 0 0 60px rgba(43,127,255,0.06)',
      }}>
        <div style={{ position:'absolute', inset:0, borderRadius:'52px', background:'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, transparent 30%, transparent 70%, rgba(255,255,255,0.03) 100%)', pointerEvents:'none', zIndex:1 }} />
        <div style={{ position:'absolute', right:'-2px', top:'160px', width:'3px', height:'52px', background:'linear-gradient(180deg, #2a2a2e, #1a1a1e)', borderRadius:'0 2px 2px 0' }} />
        <div style={{ position:'absolute', left:'-2px', top:'120px', width:'3px', height:'28px', background:'linear-gradient(180deg, #2a2a2e, #1a1a1e)', borderRadius:'2px 0 0 2px' }} />
        <div style={{ position:'absolute', left:'-2px', top:'160px', width:'3px', height:'44px', background:'linear-gradient(180deg, #2a2a2e, #1a1a1e)', borderRadius:'2px 0 0 2px' }} />
        <div style={{ position:'absolute', left:'-2px', top:'212px', width:'3px', height:'44px', background:'linear-gradient(180deg, #2a2a2e, #1a1a1e)', borderRadius:'2px 0 0 2px' }} />

        <div style={{ width:'100%', height:'100%', borderRadius:'44px', overflow:'hidden', background:'#000', position:'relative' }}>
          {/* Dynamic Island */}
          <div style={{ position:'absolute', top:'10px', left:'50%', transform:'translateX(-50%)', width:'92px', height:'26px', background:'#000', borderRadius:'20px', zIndex:20, boxShadow:'0 0 0 2px rgba(0,0,0,0.8)' }}>
            <div style={{ position:'absolute', right:'18px', top:'50%', transform:'translateY(-50%)', width:'8px', height:'8px', borderRadius:'50%', background:'radial-gradient(circle, #1a1a2e 30%, #0a0a14 70%)', boxShadow:'inset 0 0 2px rgba(43,127,255,0.3)' }} />
          </div>

          {/* iOS-style push notification — slides down from top of screen, below status bar */}
          <div style={{
            position:'absolute', top: newBooking && !bookingAccepted ? '52px' : '-90px',
            left:'12px', right:'12px', zIndex:30,
            transition:'top 0.45s cubic-bezier(0.16,1,0.3,1)',
          }}>
            <div style={{
              background:'rgba(28,28,32,0.92)', border:'1px solid rgba(255,255,255,0.1)',
              borderRadius:'16px', padding:'10px 12px',
              display:'flex', alignItems:'center', gap:'10px',
              backdropFilter:'blur(24px)',
              boxShadow:'0 8px 32px rgba(0,0,0,0.6)',
            }}>
              <div style={{ width:30, height:30, borderRadius:'8px', background:'linear-gradient(135deg, #10b981, #059669)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'14px', flexShrink:0 }}>📅</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1px' }}>
                  <span style={{ fontSize:'10px', fontWeight:700, color:'rgba(255,255,255,0.5)', letterSpacing:'0.04em', textTransform:'uppercase' }}>Mane</span>
                  <span style={{ fontSize:'9px', color:'rgba(255,255,255,0.35)' }}>now</span>
                </div>
                <div style={{ fontSize:'11px', fontWeight:600, color:WHITE, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>New Booking Request</div>
                <div style={{ fontSize:'10px', color:'rgba(255,255,255,0.5)', marginTop:'1px' }}>Jaylen W. — Precision Cut · 2:30 PM</div>
              </div>
            </div>
          </div>

          <div style={{ width:'100%', height:'100%', background:'#050810', display:'flex', flexDirection:'column' }}>
            {/* Status bar */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 22px 6px', flexShrink:0 }}>
              <span style={{ fontSize:'13px', fontWeight:600, color:WHITE, letterSpacing:'-0.02em' }}>9:41</span>
              <div style={{ display:'flex', gap:'5px', alignItems:'center' }}>
                <svg width="14" height="10" viewBox="0 0 14 10"><rect x="0" y="6" width="2.5" height="4" rx="0.5" fill={WHITE}/><rect x="3.5" y="4" width="2.5" height="6" rx="0.5" fill={WHITE}/><rect x="7" y="2" width="2.5" height="8" rx="0.5" fill={WHITE}/><rect x="10.5" y="0" width="2.5" height="10" rx="0.5" fill={WHITE}/></svg>
                <svg width="13" height="10" viewBox="0 0 13 10"><path d="M6.5 8.5a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4zM3 6.5a5 5 0 017 0" stroke={WHITE} strokeWidth="1.3" fill="none" strokeLinecap="round"/><path d="M0.5 3.5a8.5 8.5 0 0112 0" stroke={WHITE} strokeWidth="1.3" fill="none" strokeLinecap="round"/></svg>
                <div style={{ display:'flex', alignItems:'center', gap:'1px' }}>
                  <div style={{ width:'20px', height:'9px', border:'1.2px solid rgba(255,255,255,0.4)', borderRadius:'2.5px', padding:'1px' }}>
                    <div style={{ width:'80%', height:'100%', background:'#34d399', borderRadius:'1px' }} />
                  </div>
                  <div style={{ width:'1.5px', height:'4px', background:'rgba(255,255,255,0.4)', borderRadius:'0 1px 1px 0' }} />
                </div>
              </div>
            </div>

            {/* App header */}
            <div style={{ padding:'8px 18px 14px', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                <div style={{ width:34, height:34, borderRadius:'10px', background:`linear-gradient(135deg, ${GOLD}, ${BLUE})`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'14px', fontWeight:900, color:'#000' }}>M</div>
                <div>
                  <div style={{ fontSize:'15px', fontWeight:700, color:WHITE }}>Mane</div>
                  <div style={{ fontSize:'10px', color:'#7A8499' }}>Good morning, Christian</div>
                </div>
              </div>
              {/* Notification dot — pulses when new booking */}
              <div style={{ width:30, height:30, borderRadius:'50%', background: newBooking ? 'rgba(43,127,255,0.15)' : 'rgba(255,255,255,0.06)', display:'flex', alignItems:'center', justifyContent:'center', transition:'background 0.3s ease' }}>
                <div style={{ width:5, height:5, borderRadius:'50%', background: newBooking ? '#ef4444' : BLUE, boxShadow: newBooking ? '0 0 8px #ef4444' : `0 0 6px ${BLUE}`, transition:'all 0.3s ease' }} />
              </div>
            </div>

            {/* Content */}
            <div style={{ flex:1, overflow:'hidden', padding:'0 14px', position:'relative' }}>

              {/* Push notification banner — slides down from top (positioned absolutely over everything) */}

              {/* Stats row — revenue updates */}
              <div style={{ display:'flex', gap:'6px', marginBottom:'12px' }}>
                {[
                  { value: revenueDisplay, sub:'today', color:BLUE },
                  { value: bookingAccepted ? '13' : '12', sub:'clients', color:GOLD },
                  { value:'98%', sub:'complete', color:'#34d399' },
                ].map((s,i) => (
                  <div key={i} style={{ flex:1, background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'14px', padding:'12px 6px 10px', textAlign:'center' }}>
                    <div style={{ fontSize:'16px', fontWeight:800, color:s.color, transition:'all 0.5s ease' }}>{s.value}</div>
                    <div style={{ fontSize:'9px', color:'#7A8499', marginTop:'2px' }}>{s.sub}</div>
                  </div>
                ))}
              </div>

              {/* Upcoming header */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
                <span style={{ fontSize:'11px', fontWeight:700, color:WHITE }}>Upcoming</span>
                <span style={{ fontSize:'9px', color:BLUE, fontWeight:600 }}>See all</span>
              </div>

              {/* Appointments — new one slides in when accepted */}
              {[
                { name:'Marcus J.', time:'10:30', service:'Precision Cut', avatar:'M', color:'#3B82F6', always:true },
                { name:'DeShawn T.', time:'11:15', service:'Cut + Beard', avatar:'D', color:'#8B5CF6', always:true },
                { name:'Alex R.', time:'12:00', service:'Full Experience', avatar:'A', color:'#F59E0B', always:true },
                { name:'Jaylen W.', time:'2:30', service:'Precision Cut', avatar:'J', color:'#10b981', always:false },
              ].map((apt,i) => {
                const show = apt.always || bookingAccepted;
                return (
                  <div key={i} style={{
                    display:'flex', alignItems:'center', gap:'10px',
                    padding:'10px 12px', marginBottom:'6px', borderRadius:'12px',
                    background: !apt.always && bookingAccepted ? 'rgba(16,185,129,0.06)' : 'rgba(255,255,255,0.025)',
                    border: !apt.always && bookingAccepted ? '1px solid rgba(16,185,129,0.15)' : '1px solid rgba(255,255,255,0.04)',
                    maxHeight: show ? '60px' : '0px',
                    opacity: show ? 1 : 0,
                    overflow:'hidden',
                    padding: show ? '10px 12px' : '0 12px',
                    marginBottom: show ? '6px' : '0',
                    transition:'all 0.5s cubic-bezier(0.16,1,0.3,1)',
                  }}>
                    <div style={{ width:32, height:32, borderRadius:'10px', background:`linear-gradient(135deg, ${apt.color}, ${apt.color}88)`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'12px', fontWeight:700, color:WHITE, flexShrink:0 }}>{apt.avatar}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:'12px', fontWeight:600, color:WHITE }}>{apt.name}</div>
                      <div style={{ fontSize:'9px', color:'#7A8499' }}>{apt.service}</div>
                    </div>
                    <div style={{ textAlign:'right', flexShrink:0 }}>
                      <div style={{ fontSize:'11px', fontWeight:700, color:WHITE }}>{apt.time}</div>
                      <div style={{ fontSize:'8px', color: !apt.always && bookingAccepted ? '#10b981' : '#34d399', fontWeight:600 }}>
                        {!apt.always && bookingAccepted ? 'NEW' : 'CONFIRMED'}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* AI insight — text changes */}
              <div style={{
                marginTop:'8px', padding:'12px', borderRadius:'12px',
                background:'linear-gradient(135deg, rgba(43,127,255,0.06), rgba(139,92,246,0.04))',
                border:'1px solid rgba(43,127,255,0.12)',
              }}>
                <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'4px' }}>
                  <div style={{ width:16, height:16, borderRadius:'6px', background:`linear-gradient(135deg, ${BLUE}, #8B5CF6)`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'8px' }}>✦</div>
                  <span style={{ fontSize:'9px', fontWeight:700, color:BLUE, letterSpacing:'0.06em' }}>AI INSIGHT</span>
                </div>
                <div style={{ fontSize:'10px', color:'#A0AABB', lineHeight:1.5, transition:'opacity 0.3s ease' }} key={insightText}>{insightText}</div>
              </div>
            </div>

            {/* Bottom tab bar */}
            <div style={{ display:'flex', justifyContent:'space-around', padding:'10px 12px 6px', borderTop:'1px solid rgba(255,255,255,0.06)', flexShrink:0, background:'rgba(5,8,16,0.95)', backdropFilter:'blur(20px)' }}>
              {[
                { label:'Home', icon:'⌂' },
                { label:'Social', icon:'◎' },
                { label:'Clients', icon:'◉' },
                { label:'Earnings', icon:'◈' },
              ].map((tab,i) => (
                <div key={i} style={{ textAlign:'center', padding:'2px 0' }}>
                  <div style={{ fontSize:'14px', marginBottom:'2px', color: i === 0 ? BLUE : '#4a5568', lineHeight:1 }}>{tab.icon}</div>
                  <div style={{ fontSize:'9px', fontWeight: i === 0 ? 700 : 500, color: i === 0 ? BLUE : '#7A8499' }}>{tab.label}</div>
                </div>
              ))}
            </div>

            {/* Home indicator */}
            <div style={{ padding:'6px 0 8px', flexShrink:0 }}>
              <div style={{ width:'120px', height:'4px', background:'rgba(255,255,255,0.2)', borderRadius:'2px', margin:'0 auto' }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────
function Label({ children, color = BLUE }) {
  return (
    <div style={{ fontSize:'11px', fontWeight:700, letterSpacing:'0.2em', textTransform:'uppercase', color, marginBottom:'20px' }}>
      {children}
    </div>
  );
}

function SectionHeading({ children, visible, delay = 0 }) {
  return (
    <h2 className={`mane-reveal${visible ? ' in' : ''}`} style={{
      fontSize:'clamp(36px, 5vw, 56px)', fontWeight:900,
      letterSpacing:'-0.03em', lineHeight:1.1, color:WHITE,
      transitionDelay:`${delay}s`,
    }}>
      {children}
    </h2>
  );
}

function StatCounter({ value, label, index, active, color = BLUE }) {
  const match = value.match(/(\d+)/);
  const num = match ? parseInt(match[1]) : 0;
  const prefix = value.replace(/[\d+%]+.*/, '');
  const suffix = value.replace(/^[^0-9]*\d+/, '');
  const count = useCounter(num, 1800 + index * 150, active);
  return (
    <div style={{ textAlign:'center', flex:'1 1 160px', padding:'0 20px' }}>
      <div style={{ fontSize:'clamp(36px,5vw,56px)', fontWeight:900, color, letterSpacing:'-0.04em', lineHeight:1, marginBottom:'8px', textShadow:`0 0 32px ${color}55` }}>
        {prefix}{num > 0 ? count : num === 0 ? '0' : ''}{suffix}
      </div>
      <div style={{ color:OFF_WHITE, fontSize:'11px', fontWeight:600, letterSpacing:'0.15em', textTransform:'uppercase' }}>
        {label}
      </div>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────
export default function BarbersLanding() {
  const signupRef = useRef(null);
  const [form, setForm] = useState({ firstName:'', lastName:'', phone:'', email:'', city:'', platform:'' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  // Cycling industry text
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const t = setInterval(() => {
      setVisible(false);
      setTimeout(() => { setIdx(i => (i + 1) % INDUSTRIES.length); setVisible(true); }, 220);
    }, 2000);
    return () => clearInterval(t);
  }, []);

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState({});
  const handleAddon = (name) => {
    setSelectedAddons(s => ({ ...s, [name]: !s[name] }));
    setTimeout(() => scrollTo(), 150);
  };

  const [statsRef, statsIn] = useScrollReveal(0.2);
  const [problemRef, problemIn] = useScrollReveal(0.1);
  const [featuresRef, featuresIn] = useScrollReveal(0.1);
  const [pricingRef, pricingIn] = useScrollReveal(0.05);
  const [proofRef, proofIn] = useScrollReveal(0.1);
  const [ctaRef, ctaIn] = useScrollReveal(0.1);
  const [barberRef, barberIn] = useScrollReveal(0.1);

  const scrollTo = e => { e?.preventDefault(); signupRef.current?.scrollIntoView({ behavior:'smooth', block:'center' }); };
  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.email.trim()) return;
    setLoading(true);
    const addons = Object.keys(selectedAddons).filter(k => selectedAddons[k]);
    try {
      await fetch('/api/barbers/waitlist', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body:JSON.stringify({ ...form, plan: selectedPlan, addons }),
      });
    } catch (_) {}
    setLoading(false);
    setSubmitted(true);
  };

  const sec = (bg = 'transparent') => ({ padding:'clamp(100px,12vw,140px) 24px', background:bg, position:'relative' });

  return (
    <div className="mane-page" style={{ background:'transparent', minHeight:'100vh', color:WHITE, fontFamily:"'Inter',-apple-system,sans-serif", overflowX:'hidden' }}>
      <Helmet>
        <title>Mane — Booking & Business Tools for Service Professionals</title>
        <meta name="description" content="Mane gives barbers, stylists, nail techs, and every independent service pro a booking page, client management, deposits, and AI tools. Zero commission. Zero fees. Own your business." />
        <meta property="og:title" content="Mane — Built for Every Service Professional" />
        <meta property="og:description" content="Online booking, client management, deposits, and AI tools for barbers, stylists, nail techs, and more. 0% commission. Starting at $29/mo." />
        <meta property="og:url" content="https://joinmane.com/" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:image" content="https://joinmane.com/og-image-v8.png" />
        <meta name="twitter:title" content="Mane — Built for Every Service Professional" />
        <meta name="twitter:description" content="Online booking, client management, deposits, and AI tools for barbers, stylists, nail techs, and more. 0% commission. Starting at $29/mo." />
        <link rel="canonical" href="https://joinmane.com/" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Mane",
          "url": "https://joinmane.com",
          "description": "Booking and business management platform for independent service professionals — barbers, stylists, nail techs, and more.",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "offers": { "@type": "Offer", "price": "29.00", "priceCurrency": "USD" },
          "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5.0", "reviewCount": "148" },
        })}</script>
      </Helmet>
      <style>{CSS}</style>

      {/* ── NAV ── */}
      <nav style={{
        position:'fixed', top:0, left:0, right:0, zIndex:999,
        display:'flex', alignItems:'center', justifyContent:'space-between',
        padding:'0 32px', height:'60px',
        background:'rgba(5,8,16,0.92)',
        backdropFilter:'blur(16px)',
        borderBottom:'1px solid rgba(43,127,255,0.1)',
      }}>
        <ManeLogo size={32} showWordmark={true} />
        <div style={{ display:'flex', gap:'10px', alignItems:'center' }}>
          <button onClick={scrollTo} className="mane-nav-btn" style={{
            background:'transparent', border:`1px solid ${BLUE_BORDER}`,
            color:BLUE, padding:'8px 18px', borderRadius:'8px',
            fontSize:'13px', fontWeight:700, cursor:'pointer', letterSpacing:'0.04em',
            fontFamily:'inherit', lineHeight:'1',
            display:'flex', alignItems:'center',
          }}>
            Get Early Access
          </button>
          <a href="/dash/login" className="mane-nav-btn" style={{
            background:BLUE, color:WHITE, padding:'8px 18px',
            borderRadius:'8px', fontSize:'13px', fontWeight:700,
            textDecoration:'none', letterSpacing:'0.04em',
            display:'inline-flex', alignItems:'center', lineHeight:'1',
          }}>
            Sign In
          </a>
        </div>
      </nav>

      {/* ── HERO ── */}
      <style>{`
        @media(min-width:900px){.mane-hero-inner{flex-direction:row!important;text-align:left!important;align-items:center!important;}}
        @media(min-width:900px){.mane-hero-text{max-width:560px!important;}}
        @media(min-width:900px){.mane-hero-text h1{font-size:clamp(56px,6vw,82px)!important;}}
        @media(min-width:900px){.mane-hero-text p{margin:0 0 52px!important;}}
        @media(min-width:900px){.mane-hero-ctas{justify-content:flex-start!important;}}
        @media(min-width:900px){.mane-hero-eyebrow{margin-left:0!important;}}
        @media(max-width:899px){.mane-iphone-wrap{display:none!important;}}
        @media(max-width:899px){.mane-mobile-visual{display:flex!important;}}
        @media(min-width:900px){.mane-mobile-visual{display:none!important;}}
        @media(max-width:480px){
          .mane-hero-text h1{font-size:clamp(38px,10vw,56px)!important;}
          .mane-hero-ctas{flex-direction:column!important;align-items:stretch!important;}
          .mane-hero-ctas a,.mane-hero-ctas button{width:100%!important;text-align:center!important;justify-content:center!important;}
        }
      `}</style>
      <section className="mane-grid-bg" style={{
        minHeight:'100vh',
        display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
        padding:'clamp(80px,10vw,160px) clamp(16px,5vw,80px) clamp(60px,8vw,100px)',
        position:'relative', overflow:'hidden',
        background:`radial-gradient(ellipse 100% 70% at 50% -5%, rgba(43,127,255,0.16) 0%, rgba(201,168,76,0.03) 50%, transparent 70%)`,
      }}>
        {/* Animated top line */}
        <div style={{ position:'absolute', top:0, left:0, right:0, height:'2px', background:`linear-gradient(90deg, transparent 0%, ${BLUE} 40%, ${GOLD} 60%, transparent 100%)` }} />

        <div className="mane-hero-inner" style={{ position:'relative', zIndex:1, width:'100%', maxWidth:'1200px', margin:'0 auto', display:'flex', flexDirection:'column', alignItems:'center', textAlign:'center', gap:'clamp(40px,6vw,80px)' }}>

          {/* LEFT — text */}
          <div className="mane-hero-text" style={{ maxWidth:'620px', flex:'1 1 auto', width:'100%' }}>
            {/* Eyebrow */}
            <div className="mane-hero-eyebrow" style={{
              display:'inline-flex', alignItems:'center', gap:'8px',
              background:BLUE_DIM, border:`1px solid ${BLUE_BORDER}`,
              borderRadius:'99px', padding:'6px 16px', marginBottom:'32px',
              fontSize:'12px', fontWeight:600, color:BLUE, letterSpacing:'0.06em',
              opacity:0, animation:'fadeUp 0.6s ease 0.2s forwards',
            }}>
              <span style={{ width:6, height:6, borderRadius:'50%', background:BLUE, display:'inline-block', animation:'pulse-blue 2s infinite' }} />
              Now enrolling founding professionals
            </div>

            {/* Headline — FREEDOM messaging */}
            <div style={{
              opacity:0, animation:'fadeUp 0.7s ease 0.4s forwards',
              marginBottom:'24px',
            }}>
              <h1 style={{
                fontSize:'clamp(42px, 7vw, 82px)',
                fontWeight:900, lineHeight:1.0, letterSpacing:'-0.04em',
                margin:0,
                textShadow:'0 2px 32px rgba(0,0,0,0.9)',
              }}>
                Run your business.
              </h1>
              <h1 style={{
                fontSize:'clamp(42px, 7vw, 82px)',
                fontWeight:900, lineHeight:1.0, letterSpacing:'-0.04em',
                margin:'8px 0 0',
                color:GOLD,
                textShadow:`0 2px 32px rgba(0,0,0,0.9)`,
              }}>
                Live your life.
              </h1>
            </div>

            {/* Industry cycling */}
            <div style={{
              fontSize:'clamp(20px, 3vw, 28px)',
              fontWeight:700, lineHeight:1.0, letterSpacing:'-0.02em',
              height:'1.4em',
              position:'relative',
              overflow:'hidden',
              marginBottom:'24px',
              opacity:0, animation:'fadeUp 0.6s ease 0.5s forwards',
            }}>
              <span style={{
                color:BLUE,
                display:'block',
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(20px)',
                transition:'opacity 0.3s ease, transform 0.3s ease',
                whiteSpace:'nowrap',
              }}>
                Built for {INDUSTRIES[idx]}
              </span>
            </div>

            {/* Sub — freedom angle */}
            <p style={{
              color:OFF_WHITE, fontSize:'clamp(15px,2vw,18px)',
              maxWidth:'520px', margin:'0 auto 40px', lineHeight:1.8,
              opacity:0, animation:'fadeUp 0.7s ease 0.7s forwards',
            }}>
              You became your own boss to be free. But you're still chained to your phone. <span style={{color:WHITE,fontWeight:700}}>Mane automates everything</span> so your business runs while you actually live.
            </p>

            {/* CTA */}
            <div className="mane-hero-ctas" style={{ display:'flex', gap:'14px', justifyContent:'center', flexWrap:'wrap', opacity:0, animation:'fadeUp 0.7s ease 0.9s forwards' }}>
              <button onClick={scrollTo} className="mane-cta-primary">
                Claim Your Founding Spot →
              </button>
              <a href="/barber" style={{
                padding:'18px 40px', borderRadius:'12px',
                border:`1px solid ${BLUE_BORDER}`, background:'transparent',
                color:WHITE, fontWeight:700, fontSize:'15px',
                textDecoration:'none', letterSpacing:'0.04em',
                display:'inline-flex', alignItems:'center', justifyContent:'center',
                transition:'border-color 0.2s, background 0.2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor=BLUE; e.currentTarget.style.background=BLUE_DIM; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor=BLUE_BORDER; e.currentTarget.style.background='transparent'; }}
              >
                See It Live →
              </a>
            </div>
          </div>

          {/* RIGHT — iPhone 15 Pro mockup — cycles between screens */}
          <IPhoneMockup />

          {/* Mobile visual — feature cards shown instead of iPhone on small screens */}
          <div className="mane-mobile-visual" style={{ display:'none', flexDirection:'column', gap:'12px', width:'100%', marginTop:'32px' }}>
            {[
              { icon:'📅', title:'Smart Booking', desc:'Clients book 24/7. You get notified instantly.' },
              { icon:'💰', title:'Zero Commission', desc:'Every dollar from every booking is yours.' },
              { icon:'🤖', title:'AI Tools Built In', desc:'Captions, follow-ups, reminders — automated.' },
            ].map((card, i) => (
              <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:'14px', background:'rgba(43,127,255,0.06)', border:'1px solid rgba(43,127,255,0.15)', borderRadius:'14px', padding:'16px 18px' }}>
                <div style={{ fontSize:'24px', flexShrink:0, marginTop:'2px' }}>{card.icon}</div>
                <div>
                  <div style={{ color:'#fff', fontWeight:700, fontSize:'15px', marginBottom:'4px' }}>{card.title}</div>
                  <div style={{ color:'#8892A4', fontSize:'13px', lineHeight:1.5 }}>{card.desc}</div>
                </div>
              </div>
            ))}
          </div>

        </div>

        {/* Scroll dot */}
        <div style={{
          position:'absolute', bottom:'36px', left:'50%', transform:'translateX(-50%)',
          opacity:0, animation:'fadeUp 0.6s ease 1.4s forwards', cursor:'pointer',
        }} onClick={scrollTo}>
          <div style={{
            width:26, height:42, border:`2px solid ${BLUE_BORDER}`,
            borderRadius:'13px', display:'flex', justifyContent:'center', paddingTop:'8px',
          }}>
            <div style={{ width:3, height:7, background:BLUE, borderRadius:'2px', animation:'bounce 1.8s ease-in-out infinite' }} />
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section ref={statsRef} style={{
        borderTop:`1px solid ${BLUE_BORDER}`,
        borderBottom:`1px solid ${BLUE_BORDER}`,
        background:BLUE_DIM,
        padding:'clamp(48px,6vw,72px) clamp(16px,4vw,24px)',
      }}>
        <div style={{ display:'flex', justifyContent:'center', alignItems:'center', flexWrap:'wrap', gap:'clamp(24px,4vw,48px)', maxWidth:'1080px', margin:'0 auto' }}>
          {[
            { value:'3hrs', label:'Saved Daily on Admin', color: GOLD },
            { value:'$0', label:'Commission Per Booking', color: GOLD },
            { value:'24/7', label:'Booking Without You', color: BLUE },
            { value:'$29', label:'One Flat Monthly Price', color: BLUE },
          ].map((s, i) => <StatCounter key={i} {...s} index={i} active={statsIn} />)}
        </div>
      </section>

            {/* ── BUILT BY A BARBER ── */}
      <section ref={barberRef} style={{ ...sec(SURFACE), borderBottom:`1px solid ${BORDER}` }}>
        <div style={{ maxWidth:'860px', margin:'0 auto', textAlign:'center' }}>
          <div className={`mane-reveal${barberIn ? ' in' : ''}`}>
            <Label>The Real Story</Label>
            <h2 style={{ fontSize:'clamp(32px,5vw,52px)', fontWeight:900, lineHeight:1.1, letterSpacing:'-0.03em', marginBottom:'24px' }}>
              "I built this because it was<br /><span style={{ color:GOLD }}>taking from my family time."</span>
            </h2>
            <p style={{ color:OFF_WHITE, fontSize:'clamp(15px,2vw,18px)', lineHeight:1.8, maxWidth:'640px', margin:'0 auto 16px' }}>
              Christian, a working barber from Royal Palm Beach, built Mane between clients. He couldn't find a way to automate his life so he could just flow and live — not worry about finances, social media, marketing, numbers.
            </p>
            <p style={{ color:OFF_WHITE, fontSize:'clamp(15px,2vw,18px)', lineHeight:1.8, maxWidth:'640px', margin:'0 auto 48px' }}>
              He wanted to be free and know his life was still heading in the right direction. So he built the tool he needed. Now it's yours.
            </p>
            <div style={{ display:'flex', justifyContent:'center', gap:'clamp(24px,4vw,64px)', flexWrap:'wrap' }}>
              {[
                { value:'Built by', sub:'A Working Barber' },
                { value:'$0', sub:'Commission Ever' },
                { value:'$0', sub:'Hidden Fees' },
              ].map((s,i) => (
                <div key={i} style={{ textAlign:'center' }}>
                  <div style={{ fontSize:'clamp(28px,4vw,40px)', fontWeight:900, color:BLUE, letterSpacing:'-0.03em', lineHeight:1 }}>{s.value}</div>
                  <div style={{ color:OFF_WHITE, fontSize:'12px', fontWeight:600, letterSpacing:'0.12em', textTransform:'uppercase', marginTop:'6px' }}>{s.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── PROBLEM ── */}
      <section ref={problemRef} style={sec()}>
        <div style={{ maxWidth:'1080px', margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:'72px' }}>
            <Label color="#FF5555">Sound Familiar?</Label>
            <SectionHeading visible={problemIn}>
              You went independent<br />to be <span style={{color:'#FF5555'}}>free.</span>
            </SectionHeading>
            <p style={{ color:OFF_WHITE, fontSize:'clamp(15px,2vw,17px)', marginTop:'16px', lineHeight:1.7, maxWidth:'480px', margin:'16px auto 0' }}>
              But somewhere between getting booked and getting busy, freedom turned into another kind of chained.
            </p>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:'20px' }}>
            {PAIN_POINTS.map((p, i) => (
              <div key={i} className={`mane-card mane-reveal${problemIn ? ' in' : ''}`} style={{
                transitionDelay:`${0.15 + i * 0.12}s`,
                background:'rgba(255,85,85,0.04)',
                borderColor:'rgba(255,85,85,0.12)',
              }}>
                <div style={{ fontSize:'32px', marginBottom:'20px' }}>{p.icon}</div>
                <h3 style={{ fontWeight:800, fontSize:'17px', marginBottom:'10px', color:WHITE }}>{p.title}</h3>
                <p style={{ color:OFF_WHITE, fontSize:'15px', lineHeight:1.8 }}>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section ref={featuresRef} style={sec(SURFACE)}>
        <div style={{ maxWidth:'1080px', margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:'72px' }}>
            <Label>What Mane Does</Label>
            <SectionHeading visible={featuresIn}>
              Your business,<br /><span style={{color:GOLD}}>on autopilot.</span>
            </SectionHeading>
            <p style={{ color:OFF_WHITE, fontSize:'clamp(15px,2vw,17px)', marginTop:'16px', lineHeight:1.7, maxWidth:'480px', margin:'16px auto 0' }}>
              Automation that runs while you're living. So you can actually live.
            </p>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:'20px' }}>
            {FEATURES.map((f, i) => (
              <div key={i} className={`mane-card mane-reveal${featuresIn ? ' in' : ''}`} style={{ transitionDelay:`${0.08 + i * 0.08}s` }}>
                <div className="mane-feature-icon">{f.icon}</div>
                <h3 style={{ fontWeight:800, fontSize:'16px', marginBottom:'8px', color:WHITE }}>{f.title}</h3>
                <p style={{ color:OFF_WHITE, fontSize:'15px', lineHeight:1.75 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── VS COMPARISON ── */}
      <section style={{ padding:'clamp(80px,10vw,120px) 24px', background:'transparent' }}>
        <div style={{ maxWidth:'860px', margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:'56px' }}>
            <Label color="#FF5555">The Math</Label>
            <h2 style={{ fontSize:'clamp(32px,5vw,52px)', fontWeight:900, letterSpacing:'-0.03em', lineHeight:1.1 }}>
              Most service pros pay<br /><span style={{ color:'#FF5555' }}>$376/mo</span> for 6 separate apps.
            </h2>
          </div>
          <style>{`@media(min-width:600px){.mane-vs-grid{grid-template-columns:1fr 1fr!important;}}`}</style>
          <div className="mane-vs-grid" style={{ display:'grid', gridTemplateColumns:'1fr', gap:'16px' }}>
            {/* The old way */}
            <div style={{ background:'rgba(255,85,85,0.04)', border:'1px solid rgba(255,85,85,0.12)', borderRadius:'16px', padding:'24px' }}>
              <div style={{ fontSize:'11px', fontWeight:700, color:'#FF5555', letterSpacing:'0.15em', textTransform:'uppercase', marginBottom:'20px' }}>The Old Way</div>
              {[
                ['Booking Platform', '$50/mo'],
                ['DM Automation', '$15/mo'],
                ['Review Tool', '$249/mo'],
                ['Social Scheduler', '$18/mo'],
                ['Email Marketing', '$13/mo'],
                ['AI Content Tool', '$20/mo'],
              ].map(([name, price], i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                  <span style={{ color:OFF_WHITE, fontSize:'14px' }}>{name}</span>
                  <span style={{ color:'#FF5555', fontWeight:700, fontSize:'14px' }}>{price}</span>
                </div>
              ))}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:'16px', marginTop:'4px' }}>
                <span style={{ color:'#fff', fontWeight:800, fontSize:'15px' }}>Total</span>
                <span style={{ color:'#FF5555', fontWeight:900, fontSize:'22px' }}>$365/mo</span>
              </div>
            </div>
            {/* The Mane way */}
            <div style={{ background:BLUE_DIM, border:`1px solid ${BLUE_BORDER}`, borderRadius:'16px', padding:'24px' }}>
              <div style={{ fontSize:'11px', fontWeight:700, color:BLUE, letterSpacing:'0.15em', textTransform:'uppercase', marginBottom:'20px' }}>The Mane Way</div>
              {[
                ['Booking + Calendar', '✅'],
                ['AI Receptionist', '✅'],
                ['Review Builder', '✅'],
                ['Social Media AI', '✅'],
                ['Email + SMS', '✅'],
                ['Content Generation', '✅'],
              ].map(([name, check], i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                  <span style={{ color:OFF_WHITE, fontSize:'14px' }}>{name}</span>
                  <span style={{ color:'#10b981', fontWeight:700, fontSize:'14px' }}>{check} Included</span>
                </div>
              ))}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:'16px', marginTop:'4px' }}>
                <span style={{ color:'#fff', fontWeight:800, fontSize:'15px' }}>Total</span>
                <span style={{ color:BLUE, fontWeight:900, fontSize:'22px' }}>$99/mo</span>
              </div>
            </div>
          </div>
          <p style={{ textAlign:'center', color:'#8892A4', fontSize:'13px', marginTop:'20px' }}>All plans include a <strong style={{color:'#fff'}}>$149 one-time setup fee</strong>. 0% commission on every booking. Always.</p>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section ref={pricingRef} style={sec()}>
        <div style={{ maxWidth:'1080px', margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:'72px' }}>
            <Label>Pricing</Label>
            <SectionHeading visible={pricingIn}>Simple pricing.<br />No surprises.</SectionHeading>
            <p style={{ color:OFF_WHITE, fontSize:'15px', marginTop:'16px' }}>No contracts. 0% commission. Cancel anytime.</p>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:'20px', marginBottom:'32px' }}>
            {TIERS.map((t, i) => {
              const accentColor = t.featured ? BLUE : t.accent || '#8892A4';
              return (
                <div key={i} className={t.featured ? 'mane-pro-card' : ''} style={{
                  background: t.featured ? `rgba(43,127,255,0.07)` : CARD,
                  border: `1px solid ${t.featured ? 'rgba(43,127,255,0.45)' : BORDER}`,
                  borderRadius:'20px', padding:'36px 32px', position:'relative',
                  boxShadow: t.featured ? '0 0 64px rgba(43,127,255,0.15), 0 8px 32px rgba(0,0,0,0.5)' : 'none',
                  display:'flex', flexDirection:'column',
                }}>
                  {t.featured && (
                    <div style={{
                      position:'absolute', top:'-13px', left:'50%', transform:'translateX(-50%)',
                      background:BLUE, color:WHITE, fontSize:'10px', fontWeight:800,
                      letterSpacing:'0.12em', padding:'4px 16px', borderRadius:'99px',
                      whiteSpace:'nowrap', textTransform:'uppercase',
                    }}>Most Popular</div>
                  )}
                  <div style={{ fontSize:'11px', fontWeight:700, color: t.featured ? BLUE : OFF_WHITE, letterSpacing:'0.15em', textTransform:'uppercase', marginBottom:'20px' }}>{t.name}</div>
                  <div style={{ fontSize:'52px', fontWeight:900, color:WHITE, lineHeight:1, letterSpacing:'-0.04em', marginBottom:'4px' }}>{t.price}</div>
                  <div style={{ color:'#7A8499', fontSize:'13px', marginBottom:'6px' }}>{t.period}</div>
                  <div style={{ marginBottom:'28px' }}>
                    <span style={{ display:'inline-block', fontSize:'11px', fontWeight:700, color: t.featured ? BLUE : '#7a8499', background: t.featured ? 'rgba(43,127,255,0.1)' : 'rgba(255,255,255,0.05)', border: `1px solid ${t.featured ? 'rgba(43,127,255,0.25)' : 'rgba(255,255,255,0.08)'}`, borderRadius:'6px', padding:'4px 10px', letterSpacing:'0.06em' }}>
                      + {t.setup}
                    </span>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:'10px', marginBottom:'32px' }}>
                    {t.features.map((f, j) => (
                      <div key={j} style={{ display:'flex', gap:'10px', alignItems:'flex-start' }}>
                        <span style={{ color: t.featured ? BLUE : '#7A8499', fontSize:'13px', marginTop:'1px' }}>✓</span>
                        <span style={{ color: t.featured ? '#ccc' : OFF_WHITE, fontSize:'14px', lineHeight:1.5 }}>{f}</span>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => { setSelectedPlan(t.name); scrollTo(); }} style={{
                    width:'100%', padding:'13px', borderRadius:'10px', cursor:'pointer',
                    border: selectedPlan === t.name ? 'none' : t.featured ? 'none' : `1px solid ${BORDER}`,
                    background: selectedPlan === t.name ? BLUE : t.featured ? BLUE : 'transparent',
                    color: selectedPlan === t.name || t.featured ? WHITE : OFF_WHITE,
                    fontWeight:700, fontSize:'13px', letterSpacing:'0.04em',
                    fontFamily:'inherit', transition:'all 0.2s',
                    marginTop:'auto',
                  }}>
                    {selectedPlan === t.name ? '✓ Selected' : `${t.cta} →`}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Add-ons */}
          <div style={{ border:`1px solid ${BORDER}`, borderRadius:'16px', padding:'36px 32px', background:SURFACE }}>
            <div style={{ fontSize:'11px', fontWeight:700, color:'#7A8499', letterSpacing:'0.15em', textTransform:'uppercase', marginBottom:'24px' }}>
              ⚡ Power-Ups — Add to any plan
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:'16px' }}>
              {ADDONS.map((a, i) => {
                const selected = !!selectedAddons[a.name];
                return (
                  <div key={i} style={{ padding:'22px', background: selected ? 'rgba(43,127,255,0.07)' : CARD, borderRadius:'12px', border:`1px solid ${selected ? BLUE_BORDER : a.live ? BORDER : 'rgba(255,255,255,0.04)'}`, opacity: a.live ? 1 : 0.85, position:'relative', display:'flex', flexDirection:'column', transition:'all 0.2s', cursor:'pointer' }} onClick={() => handleAddon(a.name)}>
                    {!selected && !a.live && (
                      <div style={{ position:'absolute', top:'12px', right:'12px', fontSize:'9px', fontWeight:800, letterSpacing:'0.1em', padding:'2px 8px', borderRadius:'4px', textTransform:'uppercase', background:'rgba(255,255,255,0.06)', color:'#8892A4' }}>COMING SOON</div>
                    )}
                    {!selected && a.live && (
                      <div style={{ position:'absolute', top:'12px', right:'12px', fontSize:'9px', fontWeight:800, letterSpacing:'0.1em', padding:'2px 8px', borderRadius:'4px', textTransform:'uppercase', background:`rgba(16,185,129,0.1)`, color:'#10b981', border:'1px solid rgba(16,185,129,0.2)' }}>LIVE</div>
                    )}
                    {selected && (
                      <div style={{ position:'absolute', top:'12px', right:'12px', fontSize:'9px', fontWeight:800, letterSpacing:'0.1em', padding:'2px 8px', borderRadius:'4px', textTransform:'uppercase', background:BLUE_DIM, color:BLUE, border:`1px solid ${BLUE_BORDER}` }}>✓ ADDED</div>
                    )}
                    <div style={{ fontSize:'22px', marginBottom:'10px' }}>{a.icon}</div>
                    <div style={{ fontWeight:700, fontSize:'14px', marginBottom:'6px', color: selected ? WHITE : WHITE }}>{a.name}</div>
                    <div style={{ color:'#8892A4', fontSize:'13px', lineHeight:1.6, marginBottom:'14px', flex:1 }}>{a.desc}</div>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:'8px' }}>
                      <div style={{ color: selected ? BLUE : a.live ? BLUE : '#7A8499', fontWeight:800, fontSize:'13px' }}>{a.price}</div>
                      <div style={{
                        padding:'6px 14px', borderRadius:'6px',
                        background: selected ? BLUE : a.live ? BLUE : 'rgba(255,255,255,0.06)',
                        color: selected || a.live ? WHITE : OFF_WHITE,
                        fontWeight:700, fontSize:'11px', letterSpacing:'0.05em', whiteSpace:'nowrap',
                        transition:'all 0.2s',
                      }}>
                        {selected ? 'Remove' : a.live ? 'Add →' : 'Notify Me →'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <p style={{ color:'#7A8499', fontSize:'12px', textAlign:'center', marginTop:'20px' }}>Zero commission on all plans. Every dollar from bookings stays with you.</p>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section ref={proofRef} style={sec(SURFACE)}>
        <div style={{ maxWidth:'1080px', margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:'64px' }}>
            <Label>Social Proof</Label>
            <SectionHeading visible={proofIn}>Join the first wave.</SectionHeading>
            <div style={{
              display:'inline-flex', alignItems:'center', gap:'10px',
              background:BLUE_DIM, border:`1px solid ${BLUE_BORDER}`,
              borderRadius:'99px', padding:'8px 20px', marginTop:'20px',
              opacity: proofIn ? 1 : 0, transition:'opacity 0.7s ease 0.3s',
            }}>
              <span style={{ color:BLUE, fontWeight:900, fontSize:'18px' }}>Early Access</span>
              <span style={{ color:OFF_WHITE, fontSize:'13px' }}>spots filling up fast</span>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:'20px' }}>
            {TESTIMONIALS.map((t, i) => {
              const initials = t.name.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
              const avatarColors = [
                ['rgba(43,127,255,0.25)',BLUE],
                ['rgba(201,168,76,0.2)',GOLD],
                ['rgba(120,80,255,0.2)','#a78bfa'],
                ['rgba(16,185,129,0.2)','#10b981'],
              ];
              const [abg, afg] = avatarColors[i % avatarColors.length];
              return (
                <div key={i} className={`mane-card mane-reveal${proofIn ? ' in' : ''}`} style={{ transitionDelay:`${0.15 + i * 0.12}s` }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'20px' }}>
                    <div style={{ color:GOLD, fontSize:'22px', fontWeight:900, lineHeight:1, fontFamily:'Georgia,serif', opacity:0.7 }}>"</div>
                    <span style={{
                      fontSize:'9px', fontWeight:800, letterSpacing:'0.08em', textTransform:'uppercase',
                      padding:'2px 8px', borderRadius:'4px',
                      background: t.source === 'Google' ? 'rgba(66,133,244,0.1)' : BLUE_DIM,
                      color: t.source === 'Google' ? '#4285f4' : BLUE,
                      border: t.source === 'Google' ? '1px solid rgba(66,133,244,0.2)' : `1px solid ${BLUE_BORDER}`,
                    }}>{t.source}</span>
                  </div>
                  <p style={{ color:OFF_WHITE, fontSize:'15px', lineHeight:1.8, marginBottom:'24px' }}>{t.quote}</p>
                  <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                    <div className="mane-avatar" style={{ background:abg, color:afg, border:`1px solid ${afg}44` }}>{initials}</div>
                    <div>
                      <div style={{ color:WHITE, fontWeight:700, fontSize:'13px' }}>{t.name}</div>
                      <div style={{ color:'#8892A4', fontSize:'11px', marginTop:'2px', display:'flex', alignItems:'center', gap:'4px' }}>
                        <span style={{ color:GOLD, fontSize:'10px' }}>★★★★★</span>
                        <span>{t.city}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section ref={ctaRef} style={{ ...sec(), overflow:'hidden' }}>
        <div style={{
          position:'absolute', inset:0, pointerEvents:'none',
          background:`radial-gradient(ellipse 60% 50% at 50% 50%, rgba(43,127,255,0.07) 0%, transparent 70%)`,
        }} />
        <div style={{ maxWidth:'480px', margin:'0 auto', position:'relative' }} ref={signupRef}>
          {submitted ? (
            <div style={{ textAlign:'center' }}>
              <div style={{
                width:'72px', height:'72px', borderRadius:'50%',
                background:BLUE_DIM, border:`2px solid ${BLUE}`,
                display:'flex', alignItems:'center', justifyContent:'center',
                margin:'0 auto 28px', fontSize:'28px', color:BLUE,
              }}>✓</div>
              <h2 style={{ fontWeight:900, fontSize:'clamp(28px,4vw,40px)', letterSpacing:'-0.03em', marginBottom:'16px' }}>You're in.</h2>
              <p style={{ color:OFF_WHITE, fontSize:'15px', lineHeight:1.8, maxWidth:'340px', margin:'0 auto' }}>
                We'll reach out when Mane launches. Founding pros get <span style={{ color:BLUE, fontWeight:700 }}>lifetime free access</span>.
              </p>
            </div>
          ) : (
            <>
              <div style={{ textAlign:'center', marginBottom:'52px' }}>
                <Label>Founding Spots — 50 Total</Label>
                <SectionHeading visible={ctaIn}>
                  Run your business.<br /><span style={{color:GOLD}}>Live your life.</span>
                </SectionHeading>
                <p style={{ color:OFF_WHITE, fontSize:'15px', marginTop:'16px', lineHeight:1.7, opacity:ctaIn ? 1:0, transition:'opacity 0.7s ease 0.3s' }}>
                  $199 one-time → $29/mo locked forever. 0% commission. After launch: $39/mo.
                </p>
              </div>

              <form onSubmit={handleSubmit} noValidate>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'12px' }}>
                  {[['firstName','First Name','Christian','given-name'],['lastName','Last Name','Flair','family-name']].map(([n,l,p,a]) => (
                    <div key={n}>
                      <label style={{ display:'block', fontSize:'11px', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:'#7A8499', marginBottom:'8px' }}>{l}</label>
                      <input className="mane-input" name={n} value={form[n]} onChange={handleChange} placeholder={p} autoComplete={a} />
                    </div>
                  ))}
                </div>
                {[
                  ['phone','Phone','(561) 000-0000','tel','tel'],
                  ['email','Email *','you@email.com','email','email'],
                ].map(([n,l,p,t,a]) => (
                  <div key={n} style={{ marginBottom:'12px' }}>
                    <label style={{ display:'block', fontSize:'11px', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:'#7A8499', marginBottom:'8px' }}>{l}</label>
                    <input className="mane-input" name={n} type={t} value={form[n]} onChange={handleChange} placeholder={p} autoComplete={a} />
                  </div>
                ))}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'28px' }}>
                  <div>
                    <label style={{ display:'block', fontSize:'11px', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:'#7A8499', marginBottom:'8px' }}>City</label>
                    <input className="mane-input" name="city" value={form.city} onChange={handleChange} placeholder="Miami" />
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:'11px', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:'#7A8499', marginBottom:'8px' }}>Platform</label>
                    <select className="mane-input" name="platform" value={form.platform} onChange={handleChange}>
                      <option value="">Current tool...</option>
                      {['Booksy','Square','Vagaro','Fresha','None yet','Other'].map(o => <option key={o} value={o.toLowerCase()}>{o}</option>)}
                    </select>
                  </div>
                </div>
                {/* Plan + add-on summary */}
                {(selectedPlan || Object.values(selectedAddons).some(Boolean)) && (
                  <div style={{ marginBottom:'20px', padding:'16px 18px', background:BLUE_DIM, border:`1px solid ${BLUE_BORDER}`, borderRadius:'10px' }}>
                    <div style={{ fontSize:'10px', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:BLUE, marginBottom:'10px' }}>Your Selection</div>
                    {selectedPlan && (
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'6px' }}>
                        <span style={{ color:'#ccc', fontSize:'13px', fontWeight:600 }}>{selectedPlan} Plan</span>
                        <span style={{ color:BLUE, fontSize:'12px', fontWeight:700 }}>
                          {selectedPlan === 'Starter' ? '$49/mo' : selectedPlan === 'Pro' ? '$99/mo' : '$299/mo'}
                        </span>
                      </div>
                    )}
                    {Object.entries(selectedAddons).filter(([,v]) => v).map(([name]) => {
                      const addon = ADDONS.find(a => a.name === name);
                      return (
                        <div key={name} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'4px' }}>
                          <span style={{ color:'#889', fontSize:'13px' }}>+ {name}</span>
                          <span style={{ color:'#8892A4', fontSize:'12px', fontWeight:700 }}>{addon?.price}</span>
                        </div>
                      );
                    })}
                    <button type="button" onClick={() => { setSelectedPlan(null); setSelectedAddons({}); }} style={{ background:'none', border:'none', color:'#7A8499', fontSize:'11px', cursor:'pointer', marginTop:'8px', padding:0, fontFamily:'inherit' }}>
                      Clear selection
                    </button>
                  </div>
                )}

                <button type="submit" className="mane-submit" disabled={!form.firstName.trim() || !form.email.trim() || loading}>
                  {loading ? 'Sending...' : selectedPlan ? `Claim Your ${selectedPlan} Spot →` : 'Claim Your Spot →'}
                </button>
                <p style={{ color:'#6B7280', fontSize:'11px', textAlign:'center', marginTop:'16px', letterSpacing:'0.04em' }}>
                  No spam. No credit card. Just early access.
                </p>
              </form>
            </>
          )}
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ borderTop:`1px solid ${BORDER}`, padding:'32px 24px', textAlign:'center' }}>
        <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:'20px', flexWrap:'wrap' }}>
          <ManeLogo size={22} showWordmark={true} />
          <a href="/privacy" style={{ color:'#7A8499', fontSize:'12px', textDecoration:'none' }}>Privacy</a>
          <a href="/terms" style={{ color:'#7A8499', fontSize:'12px', textDecoration:'none' }}>Terms</a>
          <span style={{ color:'#6B7280', fontSize:'12px' }}>© 2026 Christian G Flair LLC</span>
        </div>
      </footer>
    </div>
  );
}
